import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { WhatsappLogo, InstagramLogo, CurrencyInr, ChatCircle } from "@phosphor-icons/react";

const STATUSES = [
  { k: "new",         lbl: "New",         tone: "bg-[var(--status-info)]" },
  { k: "qualified",   lbl: "Qualified",   tone: "bg-[var(--brand-primary)]" },
  { k: "proposal",    lbl: "Proposal",    tone: "bg-[var(--status-warning)]" },
  { k: "negotiation", lbl: "Negotiation", tone: "bg-[var(--brand-accent)]" },
  { k: "won",         lbl: "Won",         tone: "bg-[var(--status-success)]" },
  { k: "lost",        lbl: "Lost",        tone: "bg-[var(--status-danger)]" },
];

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function ChannelLeads() {
  const [data, setData] = useState({ by_status: {}, total: 0 });
  const [drag, setDrag] = useState(null);

  const load = async () => {
    const { data } = await api.get("/channel/leads");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (lead_id, status) => {
    await api.post(`/channel/leads/${lead_id}`, { status });
    load();
  };

  const onDragStart = (e, lead) => setDrag(lead);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = async (e, status) => {
    e.preventDefault();
    if (!drag || drag.status === status) return;
    await updateStatus(drag.lead_id, status);
    setDrag(null);
  };

  const sumValue = (rows) => rows.reduce((s, r) => s + (r.value_inr || 0), 0);

  return (
    <div className="p-5 space-y-4" data-testid="channel-leads-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Channel Manager</div>
          <h1 className="font-display font-bold text-3xl mt-1 text-[var(--text-primary)]">Leads Pipeline</h1>
          <div className="text-[12px] text-[var(--text-muted)] mt-1">Drag a card across stages to update its status · {data.total} total leads</div>
        </div>
        <Link to="/channel/inbox" className="span-btn" data-testid="back-to-inbox">
          <ChatCircle size={14} /> Open Inbox
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {STATUSES.map(({ k, lbl, tone }) => {
          const rows = data.by_status?.[k] || [];
          const sum = sumValue(rows);
          return (
            <div
              key={k}
              data-testid={`column-${k}`}
              onDragOver={onDragOver}
              onDrop={(e) => onDrop(e, k)}
              className="bg-[var(--surface)] border border-[var(--border)] rounded-lg overflow-hidden flex flex-col min-h-[400px]">
              <div className={`px-3 py-2 text-white font-semibold text-[11px] uppercase tracking-wider ${tone}`}>
                <div className="flex items-center justify-between">
                  <span>{lbl}</span>
                  <span className="font-mono bg-black/20 px-1.5 rounded">{rows.length}</span>
                </div>
                <div className="text-[10px] font-mono opacity-90 mt-0.5">{fmtINR(sum)}</div>
              </div>
              <div className="flex-1 p-2 space-y-2 overflow-auto">
                {rows.length === 0 && (
                  <div className="text-[11px] text-[var(--text-muted)] p-3 text-center border border-dashed border-[var(--border)] rounded">
                    Drop here
                  </div>
                )}
                {rows.map((l) => (
                  <div
                    key={l.lead_id}
                    data-testid={`lead-card-${l.lead_id}`}
                    draggable
                    onDragStart={(e) => onDragStart(e, l)}
                    className="bg-[var(--surface-2)] border border-[var(--border)] rounded p-2.5 cursor-move hover:border-[var(--brand-primary)] transition">
                    <div className="flex items-start justify-between gap-1.5 mb-1.5">
                      <div className="flex items-center gap-1 flex-1 min-w-0">
                        {l.channel === "whatsapp" && <WhatsappLogo size={12} weight="fill" className="text-[#25D366] flex-shrink-0" />}
                        {l.channel === "instagram" && <InstagramLogo size={12} className="text-[#E4405F] flex-shrink-0" />}
                        <span className="font-semibold text-[12px] truncate">{l.contact_name}</span>
                      </div>
                    </div>
                    <div className="text-[10px] text-[var(--text-muted)] font-mono truncate mb-1.5">{l.contact_handle}</div>
                    {l.value_inr > 0 && (
                      <div className="flex items-center gap-1 text-[11px] font-mono text-[var(--brand-primary)] mb-1.5">
                        <CurrencyInr size={11} weight="bold" /> {l.value_inr.toLocaleString("en-IN")}
                      </div>
                    )}
                    {l.notes && (
                      <div className="text-[10px] text-[var(--text-secondary)] line-clamp-2 leading-snug">{l.notes}</div>
                    )}
                    <Link
                      to={`/channel/inbox?conv=${l.conversation_id}`}
                      className="text-[10px] text-[var(--brand-primary)] mt-1 inline-block hover:underline"
                      data-testid={`open-conv-${l.lead_id}`}>
                      open thread →
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
